/**
 * <s-variant-media> — Scrolls the product gallery to the selected variant's photo.
 *
 * Required markup:
 *   data-media-map            — "variantId:mediaId," list written by snippets/variant-media-map.liquid.
 *   [data-product-gallery]    — The scrollable gallery (horizontal on mobile, vertical on desktop).
 *   #media-<id>               — One slide per product media.
 *   input/option[data-variant-id] — Variant picker controls (delegated, survive partial swaps).
 */
class SVariantMedia extends HTMLElement {
  #ac;
  #map = new Map();

  connectedCallback() {
    this.#ac = new AbortController();
    (this.dataset.mediaMap || '').split(',').forEach((pair) => {
      const [variantId, mediaId] = pair.split(':');
      if (variantId && mediaId) this.#map.set(variantId, mediaId);
    });
    this.addEventListener('change', this.#onChange, { signal: this.#ac.signal });

    const initial = new URL(location.href).searchParams.get('variant');
    if (initial) requestAnimationFrame(() => this.#show(initial, 'instant'));
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #onChange = (event) => {
    const control = event.target;
    if (!(control instanceof Element)) return;
    const variantId = control.dataset?.variantId || control.selectedOptions?.[0]?.dataset?.variantId;
    if (variantId) this.#show(variantId, 'smooth');
  };

  #show(variantId, behavior) {
    const mediaId = this.#map.get(String(variantId));
    if (!mediaId) return;
    const gallery = this.querySelector('[data-product-gallery]');
    const slide = this.querySelector(`#media-${mediaId}`);
    if (!gallery || !slide) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const galleryRect = gallery.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    gallery.scrollTo({
      left: gallery.scrollLeft + slideRect.left - galleryRect.left,
      top: gallery.scrollTop + slideRect.top - galleryRect.top,
      behavior: reduce ? 'instant' : behavior,
    });
  }
}

if (!customElements.get('s-variant-media')) customElements.define('s-variant-media', SVariantMedia);
